import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import OrderSSCC from "../../../../Generic/CartonSSCC";
import { CartonSSCCFrom } from "../../../../Generic/CartonSSCC/util";
import SideView from "../../../../Generic/SideView";
import DefaultViewSkeleton from "../../../../Generic/SideView/Skeleton";
import { usePurchaseOrders } from "../../../hooks/usePurchaseOrders";
import Products from "../../../Maintenance/Products";
import { OrderTabs } from "../../../utils";
import { OrderViewTab } from "../../types";
import { OrderViewDetails } from "../Details";

interface OrderViewandles {
  keyId: number | undefined;
  onCloseSidebar: () => void;
  isSidebarOpened: boolean;
}

export const OrderViewTabs = ({ keyId, isSidebarOpened, onCloseSidebar }: OrderViewandles) => {
  const { t: translate } = useTranslation();
  const {
    currenciesResponse,
    isDestinationPortFromBrazil,
    loadingOrderCartons,
    isLoadingPorts,
    portCodes,
    fetchingPurchaseOrder,
    cartons,
    purchaseOrder,
    handleUpdatePurchaseOrder,
    setCartons,
  } = usePurchaseOrders({ viewOnly: true, purchaseOrderId: String(keyId) });

  const sidebarTitle = useMemo(() => {
    let title = translate("purchaseOrder");
    const reference = purchaseOrder?.reference;

    if (reference) title = `${title} #${reference}`;

    return title;
  }, [purchaseOrder]);

  const tabs = useMemo(() => {
    const tabs: Array<OrderViewTab> = [
      {
        key: OrderTabs.DETAILS,
        label: translate(OrderTabs.DETAILS),
        loading: fetchingPurchaseOrder,
        isEmpty: !purchaseOrder,
        isOpened: false,
        render: () => <OrderViewDetails purchaseOrder={purchaseOrder} portCodes={portCodes} />,
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: OrderTabs.PRODUCTS,
        label: translate(OrderTabs.PRODUCTS),
        loading: fetchingPurchaseOrder,
        isEmpty: !purchaseOrder?.purchaseOrderProducts,
        isOpened: false,
        render: () => (
          <Products
            openedFromView
            products={purchaseOrder?.purchaseOrderProducts ?? []}
            updateOrder={handleUpdatePurchaseOrder}
            viewOnly={true}
            supplierView={true}
            portCodes={portCodes}
            loadingPorts={isLoadingPorts}
            currenciesResponse={currenciesResponse}
            purchaseOrder={purchaseOrder}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
      {
        key: OrderTabs.SSCC,
        label: translate(OrderTabs.SSCC),
        loading: fetchingPurchaseOrder || loadingOrderCartons,
        isEmpty: !cartons,
        isOpened: false,
        render: () => (
          <OrderSSCC
            openedFromView
            cartons={cartons}
            products={purchaseOrder?.purchaseOrderProducts ?? []}
            updateCartons={(cartons) => setCartons(cartons)}
            purchaseOrder={purchaseOrder}
            loadingCartons={loadingOrderCartons}
            viewOnly={{
              fields: true,
              actions: true,
            }}
            viewSupplier={true}
            calledFrom={CartonSSCCFrom.PURCHASE_ORDER}
            isLengthMandatory={isDestinationPortFromBrazil}
          />
        ),
        renderLoading: () => <DefaultViewSkeleton />,
      },
    ];

    return tabs;
  }, [cartons, purchaseOrder, fetchingPurchaseOrder, loadingOrderCartons, purchaseOrder]);

  return (
    <SideView
      isLoading={fetchingPurchaseOrder}
      isOpened={isSidebarOpened}
      onClose={onCloseSidebar}
      tabs={tabs}
      title={sidebarTitle}
      buttonsComponent={[]}
      defaultTab={OrderTabs.DETAILS}
    />
  );
};
